import cx from 'classnames';
import { DragHandleDots } from 'components/Icons';
import Constants from 'config/Constants';
import { Field } from 'formik';
import { get, intersection, toString } from 'lodash';
import { RestoreBlock } from 'pages/documents_v2/views/edit_area/blocks/RestoreBlock';
import { SelectForCompareBlock } from 'pages/documents_v2/views/edit_area/blocks/SelectForCompareBlock';
import { CheckChangesHeader } from 'pages/documents_v2/views/edit_area/CheckChangesHeader';
import { AreaContext } from 'pages/documents_v2/views/edit_area/context/AreaContext';
import { EditAreaContext } from 'pages/documents_v2/views/edit_area/context/EditAreaContext';
import { useContext, useEffect, useMemo } from 'react';
import { ArrayParam, BooleanParam, useQueryParam } from 'use-query-params';

import { useBlockUpdate } from '../../../hooks/useBlockUpdate';
import { useEntityTemplateParameters } from '../../../hooks/useEntityTemplateParameters';
import { useGetDocument } from '../../../hooks/useGetDocument';
import { BlockLayoutBlock } from './block_types/BlockLayoutBlock';
import { FileBlock } from './block_types/FileBlock';
import { LabelBlock } from './block_types/LabelBlock';
import { LinkedBlock } from './block_types/LinkedBlock';
import { ReimbursementBlock } from './block_types/ReimbursementBlock';
import { TableBlock } from './block_types/TableBlock';
import TextBlock from './block_types/TextBlock';
import { BlockToolbar } from './BlockToolbar';

export function Block({
    block,
    parentBlock,
    index,
    isDraggable = true,
    isInsideBlockLayout = false,
    searchTerm,
    selectedSearchBlock,
    readOnly = false,
    isDragging = false,
    dragHandleProps,
    areaBlocks = [],
}) {
    const editAreaContext = useContext(EditAreaContext);
    const blockUpdate = useBlockUpdate(block.key);
    const document = useGetDocument();
    const restrictions = useEntityTemplateParameters(block, 'block', document);
    const [selectedVariants = []] = useQueryParam('highligh_variants', ArrayParam);
    const [compareBlocksEnabled] = useQueryParam('compare', BooleanParam);
    const { selectedBlockId } = useContext(EditAreaContext);

    const markAsDeleted = block.deleted || block.scheduledForDeletion;
    const blockVariants = (block.documentVariants ?? []).map((variantId) => toString(variantId));
    const highlightVariant = selectedVariants.length > 0 && intersection(selectedVariants, blockVariants).length > 0;

    useEffect(() => {
        if (blockUpdate && editAreaContext) {
            const properties = get(blockUpdate, 'values.settings.properties', {});
            const documentVariants = get(blockUpdate, 'values.settings.documentVariants');

            // content, index, prop
            editAreaContext.updateBlock(properties, index, 'properties');

            if (documentVariants) {
                editAreaContext.updateBlock(documentVariants, index, 'documentVariants');
            }
        }
    }, [blockUpdate]);

    return (
        <div
            className={cx(`edit-area-block edit-area-block-type-${block.type} d-flex align-items-start`, {
                [`blockLayout-type-${block.properties.type ?? 'default'}`]:
                    block.type === Constants.blockTypes.blockLayout,
                'has-highlight-variant': highlightVariant,
                'has-no-highlight-variant': selectedVariants.length > 0 && highlightVariant === false,
                active: selectedBlockId === block.key,
                'is-deleted': markAsDeleted,
                'is-dragging': isDragging,
                'read-only': readOnly,
                'no-variants': blockVariants.length <= 1,
            })}
        >
            <>
                {compareBlocksEnabled === true && !isInsideBlockLayout && <SelectForCompareBlock block={block} />}

                <div
                    className={cx('block-drag-handle', {
                        'd-none': compareBlocksEnabled === true,
                    })}
                >
                    {isDraggable && dragHandleProps && <BlockDragHandle dragHandleProps={dragHandleProps} />}
                </div>

                <div className="edit-area-block-content-wrapper flex-grow-1 position-relative">
                    <RenderBlockInner
                        block={block}
                        parentBlock={parentBlock}
                        index={index}
                        isInsideBlockLayout={isInsideBlockLayout}
                        restrictions={restrictions}
                        isDragging={isDragging}
                        searchTerm={searchTerm}
                        selectedSearchBlock={selectedSearchBlock}
                        readOnly={readOnly}
                        areaBlocks={areaBlocks}
                    />
                </div>
            </>
        </div>
    );
}

function RenderBlockInner({
    block,
    parentBlock,
    index,
    isInsideBlockLayout,
    isDragging,
    restrictions,
    searchTerm,
    selectedSearchBlock,
    readOnly,
    areaBlocks,
}) {
    const isDeleted = block.deleted || block.scheduledForDeletion || block.deletionRejected;
    const showToolbar = !isDeleted;

    return (
        <>
            {showToolbar && (
                <BlockToolbar
                    block={block}
                    parentBlock={parentBlock}
                    index={index}
                    isInsideBlockLayout={isInsideBlockLayout}
                    readOnly={readOnly || isDeleted}
                />
            )}

            <div className="edit-area-block-content">
                {isDeleted && <RestoreBlock block={block} index={index} readOnly={readOnly} />}

                <Field name={`areaBlocks.${index}`}>
                    {({ meta }) => {
                        const dirty =
                            meta.initialValue === undefined ||
                            meta.initialValue.latestContent !== meta.value.latestContent;

                        return (
                            <BlockTypeContent
                                block={block}
                                index={index}
                                isDragging={isDragging}
                                readOnly={readOnly || isDeleted}
                                restrictions={restrictions}
                                searchTerm={searchTerm}
                                selectedSearchBlock={selectedSearchBlock}
                                areaBlocks={areaBlocks}
                                dirty={dirty}
                            />
                        );
                    }}
                </Field>
            </div>
        </>
    );
}

export function BlockTypeContent({
    block,
    index,
    isDragging = false,
    readOnly = false,
    dirty = false,
    restrictions,
    searchTerm,
    selectedSearchBlock,
    areaBlocks = [],
}) {
    const { hideChanges, baseBlocks } = useContext(EditAreaContext);
    const { selectedHistoryVersion } = useContext(AreaContext);

    const baseBlock = useMemo(() => {
        if (selectedHistoryVersion !== undefined) {
            return undefined;
        }

        return baseBlocks.find((_baseBlock) => _baseBlock.id === block.key);
    }, [baseBlocks, block.key, selectedHistoryVersion]);

    const { type } = block;

    if (type === Constants.blockTypes.text) {
        return (
            <TextBlock
                block={block}
                baseBlock={baseBlock}
                index={index}
                readOnly={readOnly}
                searchTerm={searchTerm}
                selectedSearchBlock={selectedSearchBlock}
                dirty={dirty}
            />
        );
    }

    if (type === Constants.blockTypes.blockLayout) {
        return (
            <BlockLayoutBlock
                block={block}
                index={index}
                isDragging={isDragging}
                readOnly={readOnly}
                restrictions={restrictions}
                areaBlocks={areaBlocks}
            />
        );
    }

    if (type === Constants.blockTypes.file) {
        return (
            <>
                {!hideChanges && !readOnly && baseBlock && baseBlock.changes.length > 0 && (
                    <CheckChangesHeader block={baseBlock} />
                )}
                <FileBlock block={block} baseBlock={baseBlock} index={index} readOnly={readOnly} />
            </>
        );
    }

    if (type === Constants.blockTypes.label) {
        return (
            <>
                {!hideChanges && !readOnly && baseBlock && baseBlock.changes.length > 0 && (
                    <CheckChangesHeader block={baseBlock} />
                )}
                <LabelBlock block={block} baseBlock={baseBlock} index={index} readOnly={readOnly} />
            </>
        );
    }

    if (type === Constants.blockTypes.linked) {
        return (
            <>
                {!hideChanges && !readOnly && baseBlock && baseBlock.changes.length > 0 && (
                    <CheckChangesHeader block={baseBlock} />
                )}
                <LinkedBlock block={block} baseBlock={baseBlock} index={index} readOnly={readOnly} />
            </>
        );
    }

    if (type === Constants.blockTypes.reimbursement) {
        return <ReimbursementBlock block={block} index={index} readOnly={readOnly} restrictions={restrictions} />;
    }

    if (type === Constants.blockTypes.table) {
        return (
            <TableBlock
                block={block}
                baseBlock={baseBlock}
                index={index}
                restrictions={restrictions}
                readOnly={readOnly}
                dirty={dirty}
            />
        );
    }

    return <div>Block type {type} is not supported.</div>;
}

function BlockDragHandle({ dragHandleProps }) {
    return (
        <div
            className="flex-shrink-0 drag-handle text-color"
            style={{
                fontSize: 22,
                zIndex: 100,
                borderRight: 0,
                marginLeft: -35,
                width: 35,
                transition: 'opacity 0.2s ease-in-out',
                cursor: 'grab',
            }}
            {...dragHandleProps}
        >
            <DragHandleDots />
        </div>
    );
}
